import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { UsersProvider, useUsers } from './context/UsersContext';
import { CRMProvider } from './context/CRMContext';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import LeadsList from './pages/LeadsList';
import LeadProfile from './pages/LeadProfile';
import CalendarView from './pages/CalendarView';
import UsersPage from './pages/UsersPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import '@questlabs/react-sdk/dist/style.css';

// ── Second-line defence: read hash once at module load time ──────────────────
// HashRouter rewrites window.location.hash, so we capture the FULL raw hash
// before React mounts. This runs before any component renders.
const _rawHash = window.location.hash || '';
const _isRecoveryHash =
  _rawHash.includes('type=recovery') && _rawHash.includes('access_token');

console.log('[App] window.location.hash on load:', _rawHash);
console.log('[App] _isRecoveryHash (module-level):', _isRecoveryHash);

const AppShell = () => {
  const { currentUser, loginUser, logoutUser, isLoading } = useUsers();
  const { recoveryMode, exitRecoveryMode } = useAuth();
  const [showForgot, setShowForgot] = useState(false);

  // ── Debug logs on every render ─────────────────────────────────────────────
  console.log('[AppShell] recoveryMode (AuthContext):', recoveryMode);
  console.log('[AppShell] _isRecoveryHash (module fallback):', _isRecoveryHash);

  const handleLogin = async (email, password) => {
    const user = await loginUser(email, password);
    return !!user;
  };

  // Loading spinner
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F8FAFC' }}>
        <div className="flex flex-col items-center gap-3">
          <div
            className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
            style={{ borderColor: '#2563EB', borderTopColor: 'transparent' }}
          />
          <p className="text-sm font-medium" style={{ color: '#94A3B8' }}>Cargando…</p>
        </div>
      </div>
    );
  }

  // ── Recovery mode guard ────────────────────────────────────────────────────
  // Primary: AuthContext recoveryMode (set from hash at module load + PASSWORD_RECOVERY event)
  // Fallback: module-level _isRecoveryHash in case UsersProvider/AuthProvider
  //           haven't surfaced recoveryMode yet on the very first render cycle.
  if (recoveryMode || _isRecoveryHash) {
    console.log('[AppShell] Rendering ResetPasswordPage — recoveryMode:', recoveryMode, '| _isRecoveryHash:', _isRecoveryHash);
    return <ResetPasswordPage onDone={exitRecoveryMode} />;
  }

  // Not logged in
  if (!currentUser) {
    if (showForgot) {
      return <ForgotPasswordPage onBack={() => setShowForgot(false)} />;
    }
    return (
      <LoginPage
        onLogin={handleLogin}
        onForgotPassword={() => setShowForgot(true)}
      />
    );
  }

  // Logged in — normal app
  return (
    <CRMProvider>
      <Router>
        <Layout onLogout={logoutUser}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/leads" element={<LeadsList />} />
            <Route path="/leads/:id" element={<LeadProfile />} />
            <Route path="/calendar" element={<CalendarView />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
      </Router>
    </CRMProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <UsersProvider>
        <AppShell />
      </UsersProvider>
    </AuthProvider>
  );
}

export default App;